import React, { useState, useEffect } from 'react';
import {
  Building,
  Save,
  Loader2,
  Edit2,
  AlertCircle,
  X,
} from 'lucide-react';
import { supabase, getCurrentProfileSafe } from '../lib/supabase';
import type { Profile as ProfileRecord } from '../lib/supabase';
import toast from 'react-hot-toast';
import { formatDate } from '../lib/dateUtils';
import AuditService from '../services/AuditService';

const Profile: React.FC = () => {
  const [profile, setProfile] = useState<ProfileRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
  });
  const [bodyCorporate, setBodyCorporate] = useState<any>(null);
  const [bodyCorporateLoading, setBodyCorporateLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const profileData = await getCurrentProfileSafe();
      
      if (profileData) {
        setProfile(profileData);
        setFormData({
          full_name: profileData.full_name || '',
          email: profileData.email || '',
        });

        // Fetch body corporate details if linked
        if (profileData.body_corporate_id) {
          await fetchBodyCorporate(profileData.body_corporate_id);
        }
      } else {
        // Try to get user from auth
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setFormData({
            full_name: user.user_metadata?.full_name || user.email?.split('@')[0] || '',
            email: user.email || '',
          });
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Error loading profile');
    }
    setLoading(false);
  };

  const fetchBodyCorporate = async (bodyCorporateId: string) => {
    setBodyCorporateLoading(true);
    try {
      const { data, error } = await supabase
        .from('body_corporates')
        .select('*')
        .eq('id', bodyCorporateId)
        .single();

      if (!error && data) {
        setBodyCorporate(data);
      }
    } catch (error) {
      console.error('Error fetching body corporate:', error);
    }
    setBodyCorporateLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const user = await supabase.auth.getUser();
      
      if (!user.data.user) {
        toast.error('You must be logged in');
        return;
      }

      // Update profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          full_name: formData.full_name,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.data.user.id);

      if (profileError) {
        throw profileError;
      }

      if (profile?.body_corporate_id) {
        await new AuditService(profile.body_corporate_id, user.data.user.id).logAction(
          'UPDATE', 'profile', user.data.user.id, profile, { ...profile, full_name: formData.full_name }
        );
      }

      // Update auth user metadata
      const { error: authError } = await supabase.auth.updateUser({
        data: {
          full_name: formData.full_name,
        },
      });

      if (authError) {
        console.warn('Could not update auth metadata:', authError);
        // Don't fail the whole operation if auth update fails
      }

      toast.success('Profile updated successfully!');
      setIsEditing(false);
      await fetchProfile(); // Refresh profile data
    } catch (error: any) {
      console.error('Error saving profile:', error);
      toast.error(error.message || 'Error saving profile');
    }
    setSaving(false);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'administrator': return 'bg-purple-100 text-purple-800';
      case 'staff': return 'bg-blue-100 text-blue-800';
      case 'viewer': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'administrator': return '👑';
      case 'staff': return '🛠️';
      case 'viewer': return '👁️';
      default: return '👤';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-primary-600 mx-auto" />
          <p className="mt-4 text-gray-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold">My Profile</h1>
        <p className="text-gray-500">Manage your account settings and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Profile Header */}
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-8">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center text-white text-3xl font-bold backdrop-blur-sm border-2 border-white/30">
                  {formData.full_name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="text-white">
                  <h2 className="text-xl font-bold">{formData.full_name || 'User'}</h2>
                  <p className="text-white/80 text-sm">{formData.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full bg-white/20 text-white`}>
                      {getRoleIcon(profile?.role || 'viewer')} {profile?.role ? profile.role.charAt(0).toUpperCase() + profile.role.slice(1) : 'Viewer'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Content */}
            <div className="p-6">
              {!isEditing ? (
                // View Mode
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-500">Full Name</label>
                      <p className="font-medium">{profile?.full_name || 'Not set'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Email</label>
                      <p className="font-medium">{profile?.email || 'Not set'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Role</label>
                      <p className="font-medium">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(profile?.role || 'viewer')}`}>
                          {getRoleIcon(profile?.role || 'viewer')} {profile?.role ? profile.role.charAt(0).toUpperCase() + profile.role.slice(1) : 'Viewer'}
                        </span>
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Status</label>
                      <p className="font-medium">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${profile?.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {profile?.is_active ? '✅ Active' : '❌ Inactive'}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setIsEditing(true)}
                      className="btn-primary flex items-center gap-2"
                    >
                      <Edit2 size={18} />
                      Edit Profile
                    </button>
                  </div>
                </div>
              ) : (
                // Edit Mode
                <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      disabled
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Email cannot be changed here. Contact administrator.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(profile?.role || 'viewer')}`}>
                      {getRoleIcon(profile?.role || 'viewer')} {profile?.role ? profile.role.charAt(0).toUpperCase() + profile.role.slice(1) : 'Viewer'}
                    </span>
                    <p className="text-xs text-gray-400 mt-1">
                      Role changes are managed only by an administrator.
                    </p>
                  </div>

                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className="flex-1 btn-secondary flex items-center justify-center gap-2"
                    >
                      <X size={18} />
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex-1 btn-primary flex items-center justify-center gap-2"
                    >
                      {saving ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save size={18} />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Body Corporate Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Building size={18} className="text-gray-400" />
              Body Corporate
            </h3>
            {bodyCorporateLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 size={24} className="animate-spin text-primary-600" />
              </div>
            ) : bodyCorporate ? (
              <div className="space-y-2">
                <p className="font-medium text-gray-900">{bodyCorporate.name}</p>
                <p className="text-sm text-gray-500">{bodyCorporate.complex_name}</p>
                <p className="text-sm text-gray-500">{bodyCorporate.physical_address}</p>
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-400">
                    <span className="font-medium">Linked:</span> Yes
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <AlertCircle size={32} className="text-yellow-500 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  No body corporate linked. Ask an administrator to add you to the scheme.
                </p>
              </div>
            )}
          </div>

          {/* Account Stats */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-3">Account Stats</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Member Since</span>
                <span className="font-medium">
                  {formatDate(profile?.created_at)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Last Updated</span>
                <span className="font-medium">
                  {formatDate(profile?.updated_at)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">User ID</span>
                <span className="font-mono text-xs text-gray-500 truncate max-w-[120px]">
                  {profile?.id?.slice(0, 8) || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-3">Quick Actions</h3>
            <div className="space-y-2">
              <button
                onClick={() => window.location.href = '/settings'}
                className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              >
                ⚙️ Go to Settings
              </button>
              <button
                onClick={fetchProfile}
                className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              >
                🔄 Refresh Profile
              </button>
              <button
                onClick={async () => {
                  await supabase.auth.signOut();
                  window.location.href = '/login';
                }}
                className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                🚪 Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
